/**
 * Cost round 4 on the Costs page (shapes in packages/shared/src/schemas/costs.ts): the invoices a
 * provider sent, each set beside the ledger's metered spend for that provider and month, model by
 * model; and the chargeback statement's schedule, with the last send and "Send now". Both are for
 * administrators: anyone else gets a 403 and sees nothing here.
 */

import { csvHeader, detectInvoiceFormat, invoiceProviderWords, monthWords, reconciliationWords, INVOICE_FINE_PERCENT, INVOICE_FORMAT_WORDS, INVOICE_MAX_BYTES, type ChargebackSchedule, type ChargebackSend, type InvoiceFormat, type InvoiceVariance, type InvoiceWithReconciliation } from '@aioe/shared';
import { dateLong } from '@aioe/shared/wording/dateTime';
import { Calendar, FileArrowUp, Receipt } from '@gravity-ui/icons';
import { useRef, useState } from 'react';

import { ApiError, problemText, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { useMoney } from '../money.tsx';
import { SubjectName } from './SubjectPicker.tsx';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, selectClass, StatusMark, Tick } from './ui.tsx';
import { FormField, FormSheet } from './ui/FormSheet.tsx';
import { RowMenu } from './ui/RowMenu.tsx';
import { VIEW_TONES } from './ui/Art.tsx';

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
/** Last month, the one an invoice usually arrives for. */
const lastMonth = () => {
  const d = new Date();
  return monthKey(new Date(d.getFullYear(), d.getMonth() - 1, 1));
};
const day = (iso: string) => dateLong(iso);
const forbidden = (e: unknown) => e instanceof ApiError && e.status === 403;

/** The variance as a dot and a word. */
function VarianceMark({ status }: { status: InvoiceVariance }) {
  if (status === 'fine') return <StatusMark state="connected" label="Fine" />;
  return <StatusMark state={status === 'over' ? 'error' : 'medium'} label={status === 'over' ? 'Over the ledger' : 'Under the ledger'} />;
}

/** A file picked and read, waiting to be uploaded. */
interface Picked {
  name: string;
  csv: string;
  format?: InvoiceFormat;
  /** The months the file covers, when the API asked which. */
  months?: string[];
  month?: string;
}

/** The invoices for a month, with upload and remove. Renders nothing for someone who is not an administrator. */
export function Invoices() {
  const tone = VIEW_TONES.costs!;
  const { format: money } = useMoney();
  const [month, setMonth] = useState(lastMonth);
  const list = useLoad(() => routes.invoices(month).catch((e: unknown) => (forbidden(e) ? null : Promise.reject(e))), [month]);
  const [picked, setPicked] = useState<Picked>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [removing, setRemoving] = useState<InvoiceWithReconciliation>();
  const file = useRef<HTMLInputElement>(null);
  if (list.data === null) return null;
  const invoices = list.data?.invoices ?? [];

  const pick = async (f: File | undefined) => {
    setError(undefined);
    if (!f) return;
    if (f.size > INVOICE_MAX_BYTES) {
      setError(`${f.name} is ${(f.size / 1024 / 1024).toFixed(1)} MB; an invoice export is at most ${INVOICE_MAX_BYTES / 1024 / 1024} MB.`);
      return;
    }
    const csv = await f.text();
    setPicked({ name: f.name, csv, format: detectInvoiceFormat(csvHeader(csv)) });
  };
  const upload = async () => {
    if (!picked) return;
    setBusy(true);
    setError(undefined);
    try {
      const inv = await routes.uploadInvoice({ csv: picked.csv, fileName: picked.name, ...(picked.month ? { month: picked.month } : {}) });
      setPicked(undefined);
      if (inv.month !== month) setMonth(inv.month);
      else list.reload();
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { error?: string; months?: string[] } | undefined) : undefined;
      // A file that covers several months: ask which.
      if (body?.error === 'several_months' && body.months?.length) setPicked({ ...picked, months: body.months, month: body.months.includes(month) ? month : body.months[0] });
      else setError(problemText(e));
    } finally {
      setBusy(false);
      if (file.current) file.current.value = '';
    }
  };
  const remove = async () => {
    if (!removing) return;
    try {
      await routes.removeInvoice(removing.id);
      setRemoving(undefined);
      list.reload();
    } catch (e) {
      setRemoving(undefined);
      setError(problemText(e));
    }
  };

  return (
    <>
    <ListGroup
      title="Invoices"
      count={invoices.length || undefined}
      description={`What each provider billed for the month, beside the ledger's metered spend for the same provider, model by model. Within ${INVOICE_FINE_PERCENT}% is fine. Providers bill by UTC month.`}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <input type="month" className={`${selectClass} w-44`} value={month} max={monthKey(new Date())} onChange={(e) => e.target.value && setMonth(e.target.value)} aria-label="Invoice month" />
          <Button size="sm" variant="secondary" onPress={() => file.current?.click()}>
            <FileArrowUp /> Upload
          </Button>
          <input ref={file} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => void pick(e.target.files?.[0])} aria-label="Invoice export" />
        </div>
      }
      empty={list.data && !invoices.length && !(list.error ?? error) ? <Empty tone={tone} icon={<Receipt />} title={`No invoices for ${monthWords(month)}`} body="Upload a provider's billing export for the month (Azure Cost Management, Anthropic or OpenAI) to see it beside the ledger." compact /> : undefined}
    >
      {list.error || error ? (
        <ListBlock>
          <ErrorNote error={list.error ?? error} />
        </ListBlock>
      ) : null}
      {!list.data && !list.error ? <ListBlock className="text-sm text-muted">Reading the invoices…</ListBlock> : null}
      {invoices.map((inv) => (
        <InvoiceRow key={inv.id} inv={inv} money={money} onRemove={() => setRemoving(inv)} />
      ))}
    </ListGroup>
      {/* Outside the group, so they open while the group shows its empty state. */}
      <FormSheet
        isOpen={Boolean(picked)}
        width="md"
        title="Reconcile an invoice"
        description="The export is read on this server and set beside the ledger's metered spend for its provider and month, model by model."
        onClose={() => (setPicked(undefined), setError(undefined))}
        footer={
          <>
            <Button size="sm" variant="tertiary" onPress={() => (setPicked(undefined), setError(undefined))}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" isDisabled={!picked?.format} isPending={busy} onPress={() => void upload()}>
              Reconcile
            </Button>
          </>
        }
      >
        {picked ? (
          <>
            <FormField label="File" hint={picked.format ? INVOICE_FORMAT_WORDS[picked.format] : 'Not an export the platform recognises: Azure Cost Management, Anthropic or OpenAI cost exports, or provider, model, month, amount, currency.'}>
              <span className="text-sm break-all text-foreground">{picked.name}</span>
            </FormField>
            {picked.months ? (
              <FormField label="Month the invoice is for" hint={`It covers ${picked.months.length} months; it is read for the one you choose.`}>
                <ChoiceSelect label="Month the invoice is for" className="w-full" value={picked.month} options={Object.fromEntries(picked.months.map((m) => [m, monthWords(m)]))} onChange={(m) => setPicked({ ...picked, month: m })} />
              </FormField>
            ) : null}
            <ErrorNote error={error} />
          </>
        ) : null}
      </FormSheet>
      <ConfirmDialog isOpen={Boolean(removing)} title="Remove this invoice?" confirmLabel="Remove" danger onConfirm={remove} onCancel={() => setRemoving(undefined)}>
        {removing && (
          <p>
            {invoiceProviderWords(removing.provider)}'s invoice for {monthWords(removing.month)} ({money(removing.total)}) leaves the platform, and the month's statement no longer lists it. The ledger does not change.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}

function InvoiceRow({ inv, money, onRemove }: { inv: InvoiceWithReconciliation; money: (usd: number, opts?: { digits?: number }) => string; onRemove: () => void }) {
  const [open, setOpen] = useState(false);
  const r = inv.reconciliation;
  return (
    <ListRow
      title={invoiceProviderWords(inv.provider)}
      description={
        <>
          <span className="block">{reconciliationWords(r, (n) => money(n))}.</span>
          <span className="block">
            {INVOICE_FORMAT_WORDS[inv.format]}
            {inv.fileName ? <span className="break-all">, {inv.fileName}</span> : null}, uploaded {day(inv.uploadedAt)} by <SubjectName id={inv.uploadedBy} fallback={inv.uploadedBy} />
            {inv.conversion ? `. ${inv.currency} ${inv.amount?.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} converted at US$1 = ${inv.conversion.perUsd} ${inv.currency} (${inv.conversion.source === 'closed-month' ? 'the rate the month was closed at' : inv.conversion.source === 'organisation' ? "the organisation's rate" : "the day's rate"})` : ''}
            {inv.skippedRows ? `. ${inv.skippedRows.toLocaleString()} of ${inv.rows.toLocaleString()} rows left out (other services or months)` : ''}.
          </span>
        </>
      }
      status={<VarianceMark status={r.status} />}
      compactControl
      control={
        <RowMenu
          label={`More for ${invoiceProviderWords(inv.provider)}'s invoice`}
          items={[{ id: 'models', label: open ? 'Hide the models' : `Models (${r.models.length})` }, { id: 'remove', label: 'Remove', danger: true }]}
          onAction={(id) => (id === 'models' ? setOpen(!open) : onRemove())}
        />
      }
    >
      {open && <ModelTable inv={inv} money={money} />}
    </ListRow>
  );
}

/** Per model: invoiced, the ledger, the difference and its mark. On a narrow screen each model is a short block. */
function ModelTable({ inv, money }: { inv: InvoiceWithReconciliation; money: (usd: number, opts?: { digits?: number }) => string }) {
  const r = inv.reconciliation;
  const cols = 'sm:grid-cols-[minmax(0,1fr)_7rem_7rem_7rem_9.5rem]';
  return (
    <div className="rounded-lg bg-surface-secondary/50">
      <div className={`hidden gap-3 px-3 py-2 text-xs text-muted sm:grid ${cols}`}>
        <span>Model</span>
        <span className="text-right">Invoiced</span>
        <span className="text-right">Ledger</span>
        <span className="text-right">Difference</span>
        <span>Variance</span>
      </div>
      <ul>
        {r.models.map((m) => (
          <li key={m.model} className={`grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-0.5 border-t border-separator px-3 py-2 first:border-t-0 sm:first:border-t ${cols}`}>
            <span className="mono min-w-0 truncate" title={m.model}>
              {m.model}
            </span>
            <span className="text-right tabular-nums sm:order-none">{money(m.invoicedUsd)}</span>
            <span className="text-xs text-muted tabular-nums sm:text-right sm:text-sm sm:text-foreground">
              <span className="sm:hidden">Ledger </span>
              {money(m.ledgerUsd)}
              {m.unpricedCalls ? <span className="text-muted"> · {m.unpricedCalls} unpriced</span> : null}
            </span>
            <span className="text-right text-xs tabular-nums text-muted sm:text-sm sm:text-foreground">
              {Math.abs(m.varianceUsd) < 0.005 ? '—' : `${m.varianceUsd > 0 ? '+' : '−'}${money(Math.abs(m.varianceUsd))}`}
            </span>
            <span className="col-span-2 sm:col-span-1">
              <VarianceMark status={m.status} />
            </span>
          </li>
        ))}
      </ul>
      <div className={`grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 border-t border-separator px-3 py-2 font-medium ${cols}`}>
        <span>In all</span>
        <span className="text-right tabular-nums">{money(r.invoicedUsd)}</span>
        <span className="hidden text-right tabular-nums sm:block">{money(r.ledgerUsd)}</span>
        <span className="hidden text-right tabular-nums sm:block">{Math.abs(r.varianceUsd) < 0.005 ? '—' : `${r.varianceUsd > 0 ? '+' : '−'}${money(Math.abs(r.varianceUsd))}`}</span>
        <span className="hidden sm:block">
          <VarianceMark status={r.status} />
        </span>
      </div>
    </div>
  );
}

const ordinal = (n: number) => `${n}${n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'}`;
const DAYS = Object.fromEntries(Array.from({ length: 10 }, (_, i) => [String(i + 1), `On the ${ordinal(i + 1)}`])) as Record<string, string>;
const FORMATS: Record<ChargebackSchedule['format'], string> = { csv: 'CSV attached', json: 'JSON attached' };
const FORMAT_WORDS: Record<ChargebackSchedule['format'], string> = { csv: 'with the CSV attached', json: 'with the JSON attached' };

/** The statement sent each month, its last send, and "Send now". Renders nothing for someone who is not an administrator. */
export function StatementSchedule() {
  const tone = VIEW_TONES.costs!;
  const profile = useLoad(() => routes.organisationProfile(), []);
  const sends = useLoad(() => routes.chargebackSends(5).catch((e: unknown) => (forbidden(e) ? null : Promise.reject(e))), []);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string>();
  const [sent, setSent] = useState<ChargebackSend>();
  if (sends.data === null || (profile.data && !('chargebackSchedule' in profile.data))) return null;
  const schedule = profile.data?.chargebackSchedule ?? undefined;
  const last = sent ?? sends.data?.sends[0];
  const month = lastMonth();
  const send = async () => {
    setError(undefined);
    try {
      setSent(await routes.sendChargeback(month));
      sends.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setConfirming(false);
    }
  };
  const lastLine = last ? (
    <>
      {monthWords(last.month)}, {day(last.sentAt)}
      {last.trigger === 'manual' && last.by ? (
        <>
          , sent now by <SubjectName id={last.by} fallback={last.by} />
        </>
      ) : (
        ', on schedule'
      )}
      {last.delivery === 'inbox' ? '. This platform has no mail server, so administrators were told in their inbox where it is' : ` to ${last.to.length} ${last.to.length === 1 ? 'person' : 'people'}`}
      {last.error ? `. ${last.error}` : ''}.
    </>
  ) : undefined;
  return (
    <>
    <ListGroup
      title="Statement schedule"
      description="The month's chargeback statement, sent to the people who book it, early each month in the organisation's time zone."
      actions={
        profile.data && schedule ? (
          <Button size="sm" variant="secondary" onPress={() => setConfirming(true)}>
            Send now
          </Button>
        ) : undefined
      }
      empty={profile.data && !schedule && !(profile.error ?? sends.error ?? error) ? <Empty tone={tone} icon={<Calendar />} title="No statement is sent yet" body="Send each month's chargeback statement to finance on a day early in the next month, closing the month first if nobody has." action={<Button size="sm" onPress={() => setEditing(true)}>Set a schedule</Button>} compact /> : undefined}
    >
      {profile.error || sends.error || error ? (
        <ListBlock>
          <ErrorNote error={profile.error ?? sends.error ?? error} />
        </ListBlock>
      ) : null}
      {schedule ? (
        <ListRow
          title={DAYS[String(schedule.dayOfMonth)] + ' of each month'}
          description={
            <>
              Last month's statement goes to <span className="break-words">{schedule.recipients.join(', ')}</span>, {FORMAT_WORDS[schedule.format]}
              {schedule.closeFirst ? ', the month closed first if nobody has' : ''}.
            </>
          }
          compactControl
          control={
            <Button size="sm" variant="ghost" onPress={() => setEditing(true)}>
              Change
            </Button>
          }
        />
      ) : null}
      {schedule ? <ListRow title="Last sent" description={lastLine ?? 'Nothing has been sent yet.'} status={last ? <StatusMark state={last.ok ? 'connected' : 'error'} label={last.ok ? (last.delivery === 'email' ? 'Sent' : 'Told in the inbox') : 'Not sent'} /> : undefined} /> : null}
    </ListGroup>
      {/* Outside the group, so the sheet opens from its empty state too. */}
      {editing ? (
        <ScheduleEditor
          schedule={schedule}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            profile.reload();
          }}
        />
      ) : null}
      <ConfirmDialog isOpen={confirming} title={`Send the statement for ${monthWords(month)}?`} confirmLabel="Send now" onConfirm={send} onCancel={() => setConfirming(false)}>
        <p>
          The chargeback statement for {monthWords(month)} goes now to {schedule?.recipients.join(', ')}, {schedule ? FORMAT_WORDS[schedule.format] : ''}, as it stands. Sending does not close the month.
        </p>
        <p>The schedule sends each month's statement on its own day as well.</p>
      </ConfirmDialog>
    </>
  );
}

function ScheduleEditor({ schedule, onCancel, onSaved }: { schedule?: ChargebackSchedule; onCancel: () => void; onSaved: () => void }) {
  const [dayOfMonth, setDay] = useState(String(schedule?.dayOfMonth ?? 3));
  const [recipients, setRecipients] = useState(schedule?.recipients.join(', ') ?? '');
  const [format, setFormat] = useState<ChargebackSchedule['format']>(schedule?.format ?? 'csv');
  const [closeFirst, setCloseFirst] = useState(schedule?.closeFirst ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const list = recipients
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const save = async (next: ChargebackSchedule | null) => {
    setBusy(true);
    setError(undefined);
    try {
      await routes.setChargebackSchedule(next);
      onSaved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormSheet
      title={schedule ? 'Change the statement schedule' : 'Set a statement schedule'}
      description="Each month's chargeback statement goes out on this day, in the organisation's time zone."
      onClose={onCancel}
      footer={
        <>
          {schedule ? (
            <Button size="sm" variant="danger-soft" className="me-auto" isDisabled={busy} onPress={() => void save(null)}>
              Stop sending
            </Button>
          ) : null}
          <Button size="sm" variant="tertiary" onPress={onCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isPending={busy} isDisabled={!list.length} onPress={() => void save({ dayOfMonth: Number(dayOfMonth), recipients: list, format, closeFirst })}>
            Save the schedule
          </Button>
        </>
      }
    >
      <FormField label="Send it to" hint="Email addresses, separated by commas.">
        <input className={inputClass} value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="finance@example.com, controller@example.com" />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Day of the month">
          <ChoiceSelect label="Day of the month" className="w-full" value={dayOfMonth} options={DAYS} onChange={setDay} />
        </FormField>
        <FormField label="Format">
          <ChoiceSelect label="Format" className="w-full" value={format} options={FORMATS} onChange={setFormat} />
        </FormField>
      </div>
      <Tick checked={closeFirst} onChange={setCloseFirst}>
        Close the month first if nobody has
      </Tick>
      <ErrorNote error={error} />
    </FormSheet>
  );
}
